import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, TrendingUp, Globe, Clock, Users } from 'lucide-react';

const caseStudies = {
  'gourmet-oase': {
    title: 'Gourmet-Oase 2080',
    industry: 'Gastronomie',
    image: 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=1200&h=600&fit=crop',
    challenge: 'Die Gourmet-Oase, ein exquisites Restaurant in der Innenstadt, hatte trotz hervorragender Küche kaum Online-Sichtbarkeit. Die veraltete Website war nicht mobilfähig und bot keine Möglichkeit zur Online-Reservierung. Potenzielle Gäste fanden das Restaurant nur durch Mundpropaganda.',
    solution: 'JF-NEXUS entwickelte eine moderne, mobiloptimierte Webpräsenz mit professioneller Bildsprache, integrierter Speisekarten-Darstellung und optimierter Auffindbarkeit. Die Seite wurde für lokale Suchanfragen gezielt aufbereitet.',
    results: [
      { icon: TrendingUp, label: 'Online-Reservierungen', value: '+340%' },
      { icon: Globe, label: 'Website-Besucher', value: '+520%' },
      { icon: Clock, label: 'Live in', value: '5 Tagen' },
      { icon: Users, label: 'Neue Stammkunden', value: '+85' },
    ],
    quote: 'Seit JF-NEXUS unsere Website betreut, müssen wir uns um nichts mehr kümmern. Alles funktioniert einfach.',
    quoteAuthor: 'Inhaber, Gourmet-Oase 2080',
    package: 'Business-Upgrade',
  },
  'handwerk-hub': {
    title: 'Handwerk-Hub Alpha',
    industry: 'Handwerk',
    image: 'https://images.unsplash.com/photo-1504328345606-18bbc8c9d7d1?w=1200&h=600&fit=crop',
    challenge: 'Ein traditioneller Handwerksbetrieb mit über 30 Jahren Erfahrung hatte keine digitale Präsenz. Aufträge kamen ausschließlich über persönliche Empfehlungen. Die jüngere Generation suchte jedoch online nach Handwerkern – und fand die Konkurrenz.',
    solution: 'JF-NEXUS erstellte einen professionellen One-Pager mit Projektgalerie, Leistungsübersicht und direkter Kontaktmöglichkeit. Die Seite wurde gezielt für regionale Suchanfragen optimiert und innerhalb weniger Tage live geschaltet.',
    results: [
      { icon: TrendingUp, label: 'Anfragen pro Monat', value: '+200%' },
      { icon: Globe, label: 'Regionale Sichtbarkeit', value: 'Top 3' },
      { icon: Clock, label: 'Live in', value: '3 Tagen' },
      { icon: Users, label: 'Neue Anfragen/Monat', value: '+40' },
    ],
    quote: 'Endlich finden uns auch junge Leute im Internet. Das hat unser Geschäft verändert.',
    quoteAuthor: 'Geschäftsführer, Handwerk-Hub Alpha',
    package: 'Basis-Präsenz',
  },
  'style-nova': {
    title: 'Style Studio Nova',
    industry: 'Beauty & Wellness',
    image: 'https://images.unsplash.com/photo-1560066984-138dadb4c035?w=1200&h=600&fit=crop',
    challenge: 'Ein modernes Beauty-Studio hatte zwar eine Social-Media-Präsenz, aber keine eigene Website. Termine wurden per Telefon oder Instagram-DM vereinbart – oft gingen Anfragen verloren. Das Studio wirkte weniger professionell als die Konkurrenz.',
    solution: 'JF-NEXUS entwickelte eine stilvolle Multipage-Website mit Behandlungsübersicht, Team-Vorstellung und Galerie. Die nahtlose Verbindung zu Social-Media-Kanälen sorgt für einen konsistenten Markenauftritt.',
    results: [
      { icon: TrendingUp, label: 'Buchungen', value: '+280%' },
      { icon: Globe, label: 'Online-Präsenz', value: '5 Seiten' },
      { icon: Clock, label: 'Live in', value: '7 Tagen' },
      { icon: Users, label: 'Neukunden/Monat', value: '+60' },
    ],
    quote: 'Unsere Website ist jetzt genauso schön wie unser Studio. Die Kunden lieben es!',
    quoteAuthor: 'Inhaberin, Style Studio Nova',
    package: 'Business-Upgrade',
  },
};

export default function CaseStudyPage() {
  const { slug } = useParams();
  const study = caseStudies[slug];

  if (!study) {
    return (
      <div className="min-h-screen pt-28 pb-16 flex items-center justify-center" data-testid="case-study-not-found">
        <div className="text-center">
          <h1 className="text-2xl font-semibold text-slate-900 font-['Outfit']">Case Study nicht gefunden</h1>
          <Link to="/" className="mt-4 inline-flex items-center gap-2 text-purple-600 hover:text-purple-700 transition-colors" data-testid="case-study-not-found-back">
            <ArrowLeft size={16} /> Zurück zur Startseite
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-28 pb-16" data-testid="case-study-page">
      <div className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-12">
        <Link to="/#erfolgsgeschichten" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 transition-colors mb-8" data-testid="case-study-back-link">
          <ArrowLeft size={16} /> Zurück zu Erfolgsgeschichten
        </Link>

        <div className="rounded-3xl overflow-hidden mb-12 shadow-[0_20px_40px_rgba(0,0,0,0.08)]">
          <img src={study.image} alt={study.title} className="w-full h-64 sm:h-96 object-cover" />
        </div>

        <div className="mb-12">
          <span className="text-xs tracking-[0.2em] uppercase font-bold text-purple-600 font-['Outfit']">{study.industry}</span>
          <h1 className="mt-3 text-4xl sm:text-5xl font-light tracking-tight text-slate-900 font-['Outfit']">{study.title}</h1>
          <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/60 backdrop-blur-xl border border-white/80">
            <span className="text-xs font-medium text-slate-600">Paket: <span className="text-purple-600 font-semibold">{study.package}</span></span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
          <div className="bg-white/60 backdrop-blur-2xl border border-white/80 shadow-[0_8px_32px_rgba(0,0,0,0.04)] rounded-3xl p-8">
            <span className="text-xs tracking-[0.15em] uppercase font-bold text-red-500 font-['Outfit']">Vorher &mdash; Die Herausforderung</span>
            <p className="mt-4 text-sm text-slate-600 leading-relaxed">{study.challenge}</p>
          </div>
          <div className="bg-white/60 backdrop-blur-2xl border border-white/80 shadow-[0_8px_32px_rgba(0,0,0,0.04)] rounded-3xl p-8">
            <span className="text-xs tracking-[0.15em] uppercase font-bold text-green-600 font-['Outfit']">Nachher &mdash; Die Lösung</span>
            <p className="mt-4 text-sm text-slate-600 leading-relaxed">{study.solution}</p>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
          {study.results.map((r, i) => (
            <div key={i} className="bg-white/60 backdrop-blur-2xl border border-white/80 shadow-[0_8px_32px_rgba(0,0,0,0.04)] rounded-2xl p-6 text-center" data-testid={`case-study-result-${i}`}>
              <r.icon size={20} className="text-purple-600 mx-auto mb-3" />
              <div className="text-2xl font-bold gradient-text font-['Outfit']">{r.value}</div>
              <div className="mt-1 text-xs text-slate-500">{r.label}</div>
            </div>
          ))}
        </div>

        <div className="bg-slate-900/95 backdrop-blur-3xl border border-purple-500/30 rounded-3xl p-8 lg:p-12 text-center terminal-glow mb-12">
          <blockquote className="text-lg lg:text-xl text-white font-light font-['Outfit'] leading-relaxed">
            &bdquo;{study.quote}&ldquo;
          </blockquote>
          <p className="mt-4 text-sm text-slate-400">&mdash; {study.quoteAuthor}</p>
        </div>

        <div className="text-center">
          <p className="text-slate-500 mb-6">Bereit für Ihre eigene Erfolgsgeschichte?</p>
          <Link
            to="/#kontakt"
            className="inline-flex items-center gap-3 px-8 py-4 rounded-full bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-500 text-white font-medium shadow-lg shadow-purple-500/25 hover:shadow-purple-500/40 transition-all"
            data-testid="case-study-cta"
          >
            Verbindung herstellen
          </Link>
        </div>
      </div>
    </div>
  );
}
